/**
 * THE SITE'S OWN HEADER, WHILE IT IS STANDING ON THE ARTWORK
 * ==========================================================
 *
 * The header goes transparent over an event's hero, so for the length of that
 * artwork the church's logo, its menu and its Give button are sitting on the
 * event's poster rather than on white. Woodside's teal on Flannel Fest's olive
 * is the same argument the page itself stopped having when events got their
 * own accent — so for exactly that stretch, the chrome wears the event's
 * colours too, and the moment it lifts off the artwork it is the church's
 * again.
 *
 * WHICH IS THE WHOLE RULE: every selector here is scoped to
 * `#header-outer.transparent`. Salient adds and removes that class itself as
 * the page scrolls past its first section — we do not touch it, we only say
 * what it looks like while it is on. Scrolled down, the header is white with a
 * navy logo and none of this applies, which is why an orange Give button never
 * appears on a white bar.
 *
 * THE LOGO IS RECOLOURED WITHOUT A NEW ASSET. The transparent header's logo is
 * a white PNG, which makes it a perfect stencil: its alpha channel is the
 * shape. So it is used as a CSS mask and painted in the event's ink rather
 * than swapped for an SVG. Nothing is uploaded and nothing is redrawn.
 *
 * NOTHING FROM A DATABASE BECOMES CSS TEXT. The stylesheet below is a
 * constant. Every value staff chose arrives as a custom property, and only
 * after `isHexColor` for a colour or `logoUrlFor` for the mask — the same gate
 * the page's own accent goes through.
 *
 * THE FOOTER IS NOT IN THIS. It was, briefly: the church's navy with the
 * event's hue mixed through it, on the reasoning that it was the last block
 * on the page not wearing the event's colour. On a phone it read as mud. The
 * header earns its colour by physically sitting on the artwork; the footer
 * sits on its own ground three screens later and is the church's furniture,
 * so it stays the church's.
 *
 * IT IS OUR MESS TO CLEAN UP. The widget is a guest on somebody else's page:
 * `paintChrome` returns the undo, the properties and the stylesheet come off
 * together, and an event with no colour of its own never writes anything at
 * all.
 */

import { isHexColor } from './accent';

/** Flags on <html> that turn each block on. Absent = the theme's own colours. */
export const CHROME_FLAG = 'data-ed-chrome';
export const CHROME_LOGO_FLAG = 'data-ed-chrome-logo';

export interface ChromeVars {
  '--ed-chrome-ink': string;
  '--ed-chrome-accent': string;
  '--ed-chrome-on-accent': string;
}

/**
 * The mask URL, or null.
 *
 * Only an http(s) address from this page's own origin, and only one with no
 * quote, backslash, parenthesis or whitespace in it — because unlike a colour,
 * this one does end up inside a `url()`. A logo served from somewhere else is
 * not something the widget should be painting.
 */
export function logoUrlFor(src: string | null | undefined, origin: string): string | null {
  if (!src) return null;
  let url: URL;
  try {
    url = new URL(src, origin);
  } catch {
    return null;
  }
  if (url.protocol !== 'https:' && url.protocol !== 'http:') return null;
  if (url.origin !== origin) return null;
  if (/["'\\()\s]/.test(url.href)) return null;
  return `url("${url.href}")`;
}

/**
 * The three values the header wears, or null when the event has no colour.
 *
 * `ink` is what the event puts over its own artwork, which is exactly the
 * question the logo and the menu are asking. `onAccent` is what can be read on
 * the accent itself, which is the Give button's problem and not the same
 * question — see lib/accent.ts.
 */
export function chromeVarsFor(
  accent: string | null,
  ink: string | null,
  onAccent: string | null
): ChromeVars | null {
  if (!isHexColor(accent)) return null;
  return {
    '--ed-chrome-ink': isHexColor(ink) ? ink : '#ffffff',
    '--ed-chrome-accent': accent,
    '--ed-chrome-on-accent': isHexColor(onAccent) ? onAccent : '#ffffff',
  };
}

/**
 * A constant. No value from the page, the API or the database is ever spliced
 * into this string.
 *
 * `#header-outer#header-outer` is not a typo. The theme's own rule for the
 * Give button's fill is in a stylesheet this script cannot read and carries
 * `!important` of its own, so the id is repeated to outweigh it — measured
 * against the live header rather than guessed, because a specificity fight you
 * lose is a page that looks untouched and tells you nothing.
 *
 * The transitions are what turns the swap into the page arriving rather than
 * the page flickering: the header paints in the church's teal, the event's
 * colours land when its data does, and the difference reads as the artwork
 * loading in.
 */
export const CHROME_CSS = `
html[${CHROME_FLAG}] #header-outer.transparent nav > ul > li > a,
html[${CHROME_FLAG}] #header-outer.transparent nav .menu-title-text,
html[${CHROME_FLAG}] #header-outer.transparent .slide-out-widget-area-toggle a::before,
html[${CHROME_FLAG}] #header-outer#header-outer.transparent nav ul.sf-menu > li.button_solid_color > a::before {
  transition: background-color 450ms ease, color 450ms ease;
}
html[${CHROME_FLAG}] #header-outer.transparent nav > ul > li > a,
html[${CHROME_FLAG}] #header-outer.transparent nav .menu-title-text {
  color: var(--ed-chrome-ink) !important;
}
html[${CHROME_FLAG}] #header-outer.transparent .slide-out-widget-area-toggle a::before {
  background-color: var(--ed-chrome-accent) !important;
}
html[${CHROME_FLAG}] #header-outer#header-outer.transparent nav ul.sf-menu > li.button_solid_color > a::before {
  background-color: var(--ed-chrome-accent) !important;
}
html[${CHROME_FLAG}] #header-outer#header-outer.transparent nav ul.sf-menu > li.button_solid_color > a,
html[${CHROME_FLAG}] #header-outer#header-outer.transparent nav ul.sf-menu > li.button_solid_color > a .menu-title-text {
  color: var(--ed-chrome-on-accent) !important;
}
html[${CHROME_LOGO_FLAG}] #header-outer.transparent #logo {
  position: relative;
}
html[${CHROME_LOGO_FLAG}] #header-outer.transparent #logo img.starting-logo {
  opacity: 0 !important;
}
html[${CHROME_LOGO_FLAG}] #header-outer.transparent #logo::after {
  content: '';
  position: absolute;
  inset: 0;
  pointer-events: none;
  -webkit-mask: var(--ed-chrome-logo) left center / contain no-repeat;
  mask: var(--ed-chrome-logo) left center / contain no-repeat;
  background-color: var(--ed-chrome-ink);
  transition: background-color 450ms ease;
}
@media (prefers-reduced-motion: reduce) {
  html[${CHROME_FLAG}] #header-outer.transparent nav > ul > li > a,
  html[${CHROME_FLAG}] #header-outer.transparent nav .menu-title-text,
  html[${CHROME_FLAG}] #header-outer.transparent .slide-out-widget-area-toggle a::before,
  html[${CHROME_LOGO_FLAG}] #header-outer.transparent #logo::after,
  html[${CHROME_FLAG}] #header-outer#header-outer.transparent nav ul.sf-menu > li.button_solid_color > a::before {
    transition: none;
  }
}
`;

const STYLE_ID = 'ed-chrome-accent';
/**
 * The transparent header's logos: white artwork, which is the stencil.
 *
 * There is more than one. Salient ships a full wordmark and a mark-only
 * version and swaps them at its own breakpoint, so which one is the logo is a
 * question about the current window rather than a selector — masking the
 * wordmark at every width is how the phone ended up with the desktop logo.
 */
const LOGO_SELECTOR = '#header-outer #logo img.starting-logo';

/**
 * The one the theme is currently showing, by layout rather than by name: the
 * hidden variant is laid out at zero width, and that stays true after our own
 * rules have taken its opacity away.
 */
export function widestOf<T extends { getBoundingClientRect(): { width: number } }>(
  candidates: readonly T[]
): T | null {
  let best: T | null = null;
  let widest = 0;
  for (const el of candidates) {
    const { width } = el.getBoundingClientRect();
    if (width > widest) {
      widest = width;
      best = el;
    }
  }
  return best;
}

/**
 * Dress the site's header in the event's colours, and hand back the undo.
 *
 * Returns a no-op when there is nothing to do — no event colour, no header on
 * this page (the dev app), or a document that is not there yet — so callers
 * never have to ask which of those it is.
 */
export function paintChrome(doc: Document, vars: ChromeVars | null): () => void {
  const root = doc.documentElement;
  if (!root) return () => {};

  const clear = () => {
    root.removeAttribute(CHROME_FLAG);
    root.removeAttribute(CHROME_LOGO_FLAG);
    for (const name of ['--ed-chrome-ink', '--ed-chrome-accent', '--ed-chrome-on-accent']) {
      root.style.removeProperty(name);
    }
    root.style.removeProperty('--ed-chrome-logo');
    doc.getElementById(STYLE_ID)?.remove();
  };

  if (!vars || !doc.querySelector('#header-outer')) {
    clear();
    return () => {};
  }

  let style = doc.getElementById(STYLE_ID);
  if (!style) {
    style = doc.createElement('style');
    style.id = STYLE_ID;
    style.textContent = CHROME_CSS;
    doc.head.appendChild(style);
  }

  for (const [name, value] of Object.entries(vars)) root.style.setProperty(name, value);

  // The logo is separately flagged: without a mask to draw, the block that
  // hides the white PNG would leave a painted rectangle where the logo was.
  // And it is re-read on resize, because crossing the theme's breakpoint
  // swaps the wordmark for the mark and the stencil has to follow.
  const pickLogo = () => {
    const logo = widestOf([...doc.querySelectorAll<HTMLImageElement>(LOGO_SELECTOR)]);
    const mask = logoUrlFor(logo?.currentSrc || logo?.src, doc.location?.origin ?? '');
    if (mask) {
      root.style.setProperty('--ed-chrome-logo', mask);
      root.setAttribute(CHROME_LOGO_FLAG, '');
    } else {
      root.style.removeProperty('--ed-chrome-logo');
      root.removeAttribute(CHROME_LOGO_FLAG);
    }
  };
  pickLogo();

  const view = doc.defaultView;
  view?.addEventListener('resize', pickLogo);

  // Last, so the transition has something to run from.
  root.setAttribute(CHROME_FLAG, '');
  return () => {
    view?.removeEventListener('resize', pickLogo);
    clear();
  };
}
